import { useState, useEffect, useRef } from 'react';
import { useIndexedDB } from '@thd-olt-global/thd-storage-utils';
import { getUserName } from '../utils/assistant-context-utils';
import { getDefaultSession, getSessionId, sessionIsExpired } from '../utils/session-utils';

const getInitialMessage = () => {
  const authUser = getUserName();
  return {
    type: 'bot',
    content: {
      responseSummary: {
        // eslint-disable-next-line max-len
        summaryAnswer: `Hi${authUser ? `  ${authUser}` : ''}! Need help with a product, project, or question? I'm here to assist. What can I help you with today?`
      }
    }
  };
};

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (withPersistence = true) => {
  const session = useRef(null);
  const [hasSessionInteractedWithFab, setHasSessionInteractedWithFab] = useState(false);
  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryAvailable, setIsHistoryAvailable] = useState(false);
  /* eslint-disable camelcase */
  const {
    getByKey: iDBGetByKey_session,
    getAll: iDBGetAll_session,
    add: iDBAdd_session,
    update: iDBUpdate_session,
    cursor: iDBCursor_session,
  } = useIndexedDB('sessions');
  const {
    getAll: iDBGetAll_messages,
    add: iDBAdd_message,
    update: iDBUpdate_message,
    clear: iDBClear_message,
    cursor: iDBCursor_message,
  } = useIndexedDB('messages');
  /* eslint-enable camelcase */

  const handleSaveMessage = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.current.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
    };
    if (!isStorageAvailable.current) {
      setMessages([...messages, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.current.sessionId) return Promise.reject(new Error('Session ID not available'));

    return iDBAdd_message(updatedMessage)
      .then(iDBGetAll_messages)
      .then((savedMessages) => {
        setMessages(savedMessages);
        return savedMessages.at(-1);
      });
  };

  const handleLoadMessages = async (retrievedMessages) => {
    setIsLoading(true);
    if (retrievedMessages.length === 0) {
      await handleSaveMessage(getInitialMessage());
    } else {
      setMessages(retrievedMessages);
    }
    setIsLoading(false);
  };

  const resetMessages = async () => {
    await iDBClear_message();
    setMessages([]);
    await handleSaveMessage(getInitialMessage());
  };

  const setFeedbackProvided = async (message) => {
    const updatedMessage = JSON.parse(JSON.stringify(message));
    updatedMessage.feedbackProvided = true;

    await iDBUpdate_message(updatedMessage);
  };

  const updateSession = async (sessionUpdates) => {
    let retrievedSession = await iDBGetByKey_session(getSessionId());
    retrievedSession = {
      ...retrievedSession,
      ...sessionUpdates
    };

    await iDBUpdate_session(retrievedSession).then(() => {
      session.current = retrievedSession;
      setHasSessionInteractedWithFab(retrievedSession.hasSessionInteractedWithFab);
    });
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const cleanIndexDb = async () => {
    // Remove expired sessions
    await iDBCursor_session((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!cursor.value.sessionId || sessionIsExpired(cursor.value)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });

    // Remove messages that have no session associated in db
    const retrievedSessions = await iDBGetAll_session();
    const retrievedSessionIds = retrievedSessions.map(({ sessionId }) => sessionId);

    await iDBCursor_message((event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (!retrievedSessionIds.includes(cursor.value.sessionId)) {
          cursor.delete();
        }
        cursor.continue();
      }
    });
  };

  const init = async () => {
    const savedSession = await iDBGetByKey_session(getSessionId());
    let activeSession = savedSession;

    if (!activeSession) {
      const newSession = getDefaultSession();
      await iDBAdd_session(newSession);
      activeSession = newSession;
    }

    session.current = activeSession;
    setHasSessionInteractedWithFab(activeSession.hasSessionInteractedWithFab);
    isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';

    await cleanIndexDb();
    const savedMessages = await iDBGetAll_messages(savedSession);

    await handleLoadMessages(savedMessages);
  };

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = new BroadcastChannel('magic-apron-new-message');
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = () => {
            iDBGetAll_messages().then(handleLoadMessages);
          };
        }
      })
      .catch(() => {});

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
      }
    });
  }, []);

  useEffect(() => {
    // TODO: Is this actually needed? Can we just check for if messages exist? We can't have messages without session.
    if (session.current && messages) {
      setIsHistoryAvailable(true);
    }
  }, [messages]);

  return {
    isHistoryAvailable,
    messages,
    saveMessage: handleSaveMessage,
    historyLoading: isLoading,
    clearHistory: resetMessages,
    setFeedbackProvided,
    broadCastMessageUpdate,
    drawerShouldOpenOnLoad: session.current?.shouldOpenDrawer || false,
    hasSessionInteractedWithFab: hasSessionInteractedWithFab || false,
    updateSession
  };
};
